import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Download, Copy } from 'lucide-react';
import Button from '../Button';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { Cell } from 'recharts';

interface VisualizationData {
  category?: string;
  name?: string;
  value: string;
  percentage?: string;
  breakdown?: string;
  description?: string;
}

interface Visualization {
  type: 'bar_chart' | 'pie_chart' | 'table';
  title: string;
  data: VisualizationData[];
}

interface BarChartProps {
  visualization: Visualization;
  questionType?: string;
  className?: string;
  optionImages?: Record<string, string>;
  imageAttachmentLevel?: 'question' | 'option';
  isExporting?: boolean;
}

// Custom tooltip to show original value as fraction and optional description
const CustomTooltip = ({ active, payload, total, isMaxDiff }: { active?: boolean, payload?: any[], total: number, isMaxDiff?: boolean }) => {
  if (active && payload && payload.length > 0) {
    const item = payload[0].payload;
    return (
      <div className="bg-white p-2 border rounded shadow text-xs max-w-xs">
        <div className="font-medium">{item.name}</div>
        <div className="text-gray-600">
          {isMaxDiff
            ? `Net Score: ${item.percentage.toFixed(1)}`
            : `Score: ${item.originalValue}/${total} (${item.percentage.toFixed(2)}%)`}
        </div>
        {item.description && (
          <div className="text-gray-500 mt-1 border-t pt-1">{item.description}</div>
        )}
      </div>
    );
  }
  return null;
};

// Custom label for value at end of bar
const createBarEndLabel = (showPercent: boolean) => (props: any) => {
  const { x, y, width, value } = props;
  return (
    <text
      x={x + width + 8}
      y={y + 16}
      fill="#222"
      fontSize={12}
      textAnchor="start"
      alignmentBaseline="middle"
    >
      {value.toFixed(1)}{showPercent ? '%' : ''}
    </text>
  );
};

// Custom bar shape to color the first 2px as Y-axis color
const CustomBarShape = (props: any) => {
  const { x, y, width, height, fill } = props;
  const p = 1;
  if (width <= p) {
    return <rect x={x} y={y} width={width} height={height} fill="#000" />;
  }
  return (
    <g>
      <rect x={x} y={y} width={p} height={height} fill="#000" />
      <rect x={x + p} y={y} width={width - p} height={height} fill={fill} />
    </g>
  );
};

const BarChart: React.FC<BarChartProps> = ({ 
  visualization, 
  questionType = 'default',
  className = '',
  optionImages,
  imageAttachmentLevel: _imageAttachmentLevel,
  isExporting = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  
  if (!visualization?.data) return null;
  
  const API_URL = import.meta.env.VITE_API_URL || '';
  
  // Check if we have images for options (regardless of image_attachment_level)
  const hasOptionImages = optionImages && Object.keys(optionImages).length > 0;

  // Detect backend-supplied percentages (e.g. "100.0%" / "12.5"). When every
  // item carries one, we trust it as the source of truth — this is required
  // for multiple_choice where sum-of-counts > respondents and the recompute
  // would otherwise show "share of selections" instead of "% of respondents".
  const hasPreComputedPct = visualization.data.every(
    d => typeof d.percentage === 'string' && /^\d+(?:\.\d+)?%?$/.test(String(d.percentage).trim()),
  );

  // Parse and scale data
  const scaledData = visualization.data.map(item => {
    const value = parseFloat(item.value) || 0;
    const preComputedPct = hasPreComputedPct
      ? parseFloat(String(item.percentage).replace('%', '').trim())
      : null;
    return {
      name: item.category || item.name || '',
      originalValue: item.value,
      value: value,
      preComputedPct,
      description: item.description
    };
  });

  // Check if we have negative values (for MaxDiff net scores) or if this is a MaxDiff question
  const hasNegativeValues = scaledData.some(item => item.value < 0);
  const isMaxDiff = questionType === 'max_diff';
  const isPreCalculatedPercentage = hasNegativeValues || isMaxDiff;

  const sumOfValues = scaledData.reduce((acc, item) => acc + item.value, 0);

  // Derive the respondent count from pre-computed percentages so the tooltip
  // shows "21/21 (100%)" instead of "21/59 (35.6%)" for multi-choice. Pick the
  // max-derived value across items for numerical stability.
  let respondentCount = 0;
  if (hasPreComputedPct && !isPreCalculatedPercentage) {
    for (const it of scaledData) {
      if (it.preComputedPct && it.preComputedPct > 0 && it.value > 0) {
        respondentCount = Math.max(
          respondentCount,
          Math.round((it.value * 100) / it.preComputedPct),
        );
      }
    }
  }

  const total = isPreCalculatedPercentage
    ? 0
    : (respondentCount > 0 ? respondentCount : sumOfValues);

  // Calculate percentage data
  const percentData = scaledData.map(item => ({
    ...item,
    percentage: isPreCalculatedPercentage
      ? item.value
      : (hasPreComputedPct && item.preComputedPct !== null
          ? item.preComputedPct
          : (sumOfValues > 0 ? (item.value / sumOfValues) * 100 : 0)),
    isNegative: item.value < 0
  }));

  const handleCopy = async () => {
    if (!chartRef.current) return;
    try {
      const canvas = await html2canvas(chartRef.current, { backgroundColor: '#fff' });
      canvas.toBlob(async (blob) => {
        if (blob) {
          await navigator.clipboard.write([
            new window.ClipboardItem({ 'image/png': blob })
          ]);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }
      });
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleDownload = async () => {
    if (!chartRef.current) return;
    const canvas = await html2canvas(chartRef.current, { backgroundColor: '#fff', scale: 2 });
    const link = document.createElement('a');
    link.download = 'chart.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className={`bg-white p-4 rounded-lg border border-gray-200 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-700">{visualization.title}</h3>
        <div className="flex gap-2 no-print">
          <div className="relative">
            <Button
              size="sm"
              variant="ghost"
              icon={<Download className="h-4 w-4" />}
              onClick={handleDownload}
              title="Download chart as image"
              onMouseEnter={() => setShowTooltip('download')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              {''}
            </Button>
            {showTooltip === 'download' && (
              <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                Download chart as image
              </div>
            )}
          </div>
          <div className="relative">
            <Button
              size="sm"
              variant="ghost"
              icon={<Copy className="h-4 w-4" />}
              onClick={handleCopy}
              title="Copy chart as image"
              onMouseEnter={() => setShowTooltip('copy')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              {''}
            </Button>
            {showTooltip === 'copy' && (
              <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                Copy chart as image
              </div>
            )}
          </div>
          {copied && (
            <span className="text-green-600 text-xs ml-1">Copied!</span>
          )}
        </div>
      </div>

      <div ref={chartRef} className="w-full h-full">
        <ResponsiveContainer width="100%" height={hasOptionImages ? Math.max(300, scaledData.length * 55) : 300}>
          <RechartsBarChart
            data={percentData}
            layout="vertical"
            margin={{
              top: 0,
              right: 52,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number"
              domain={isPreCalculatedPercentage ? undefined : [0, 100]}
              tickFormatter={(value) => isMaxDiff ? `${value.toFixed(0)}` : `${value.toFixed(0)}%`}
              fontSize={12}
              axisLine={{ stroke: "#000", strokeWidth: 2 }}
            />
            <YAxis 
              type="category"
              dataKey="name"
              width={hasOptionImages ? 60 : 200}
              fontSize={12}
              stroke="#000"
              strokeWidth={2}
              interval={0}
              tick={({ x, y, payload, index }: any) => {
                const label = payload.value;
                
                let imageUrl = optionImages?.[label] ?? null;
                if (!imageUrl && optionImages) {
                  const imageValues = Object.values(optionImages);
                  if (index < imageValues.length) {
                    imageUrl = imageValues[index];
                  }
                }
                const fullImageUrl = imageUrl ? (imageUrl.startsWith('http') ? imageUrl : `${API_URL}${imageUrl}`) : null;
                
                if (fullImageUrl) {
                  if (isExporting) {
                    return (
                      <g transform={`translate(${x},${y})`}>
                        <text
                          x={-10}
                          y={0}
                          textAnchor="end"
                          fontSize={12}
                          fill="#666"
                          alignmentBaseline="middle"
                        >
                          {label}
                        </text>
                      </g>
                    );
                  }
                  const clipId = `clip-opt-${index}-${label.replace(/[^a-zA-Z0-9]/g, '').slice(0, 16)}`;
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <defs>
                        <clipPath id={clipId}>
                          <rect x={-48} y={-20} width={40} height={40} rx={4} />
                        </clipPath>
                      </defs>
                      <image
                        href={fullImageUrl}
                        x={-48}
                        y={-20}
                        width={40}
                        height={40}
                        preserveAspectRatio="xMidYMid slice"
                        clipPath={`url(#${clipId})`}
                      />
                      <rect x={-48} y={-20} width={40} height={40} rx={4}
                        fill="none" stroke="#e5e7eb" strokeWidth={1} />
                    </g>
                  );
                } else {
                  // Render text label as before
                  const maxLen = 24;
                  const displayLabel =
                    typeof label === 'string' && label.length > maxLen
                      ? label.slice(0, maxLen) + '...'
                      : label;
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <foreignObject x={-180} y={-10} width={200} height={32}>
                        <div
                          style={{
                            fontSize: 12,
                            color: '#666',
                            textAlign: 'left',
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-line',
                            lineHeight: 1,
                            maxHeight: 32,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start'
                          }}
                        >
                          {displayLabel}
                        </div>
                      </foreignObject>
                    </g>
                  );
                }
              }}
            />
            <Tooltip 
              wrapperStyle={{ pointerEvents: 'auto' }}
              content={<CustomTooltip total={total} isMaxDiff={isMaxDiff} />}
            />
            <Bar 
              dataKey="percentage" 
              fill={isPreCalculatedPercentage ? undefined : "#07E5D1"} 
              barSize={30} 
              label={createBarEndLabel(!isMaxDiff)} 
              shape={isPreCalculatedPercentage ? undefined : <CustomBarShape />}
            >
              {isPreCalculatedPercentage && percentData.map((_entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill='#07E5D1' 
                />
              ))}
            </Bar>
          </RechartsBarChart>
        </ResponsiveContainer>
        {questionType === 'conjoint_analysis' && scaledData.some(d => d.description) && (
          <div className="mt-4 border-t border-gray-200 pt-3">
            <div className="text-xs font-semibold text-gray-600 mb-2">
              Profile breakdown
            </div>
            <div className="space-y-1">
              {scaledData
                .filter(d => d.description)
                .map((d, i) => (
                  <div
                    key={`profile-legend-${i}`}
                    className="flex flex-wrap items-baseline gap-x-2 text-xs"
                  >
                    <span className="font-medium text-gray-700 whitespace-nowrap">
                      {d.name}
                    </span>
                    <span className="text-gray-500">{d.description}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BarChart;